/**
 * Phone E.164 helpers used by Auth OTP and profile writes.
 *
 * Dial codes that use a national trunk prefix "0" (RO, GB, DE, …) must drop
 * that 0 when combined with the country code. `+40` + `0712…` is `+40712…`,
 * never `+400712…`.
 */

/** Longest-first dial codes whose national numbers start with a trunk 0. */
const TRUNK_ZERO_DIALS = [
  '964',
  '966',
  '971',
  '212',
  '40',
  '44',
  '49',
  '33',
  '34',
  '90',
  '98',
  '61',
  '20',
] as const;

export function normalizeDialDigits(dial: string | null | undefined): string {
  return String(dial || '').trim().replace(/^\+/, '').replace(/\D/g, '');
}

/**
 * Normalize a phone string to E.164 with a leading '+'.
 * Used when writing to user_profile.phone and as a last-pass before Auth OTP.
 */
export function normalizePhoneE164(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  const trimmed = String(raw).trim();
  if (!trimmed) return null;

  let cleaned = trimmed.replace(/[^0-9+]/g, '');
  cleaned = cleaned.replace(/^\++/, '+');
  cleaned = cleaned.replace(/(?!^)\+/g, '');

  if (!cleaned) return null;

  if (cleaned.startsWith('+')) {
    return cleaned.length >= 8 ? stripInternationalTrunkZero(cleaned) : null;
  }

  const digits = cleaned;
  if (digits.length === 10) {
    return `+1${digits}`;
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  }
  if (digits.length >= 10) {
    return stripInternationalTrunkZero(`+${digits}`);
  }

  return `+${digits}`;
}

/** Drop a national trunk 0 that was left on after the country code (e.g. +4007… → +407…). */
export function stripInternationalTrunkZero(e164: string): string {
  const phone = e164.startsWith('+') ? e164 : `+${e164}`;
  const rest = phone.slice(1);
  for (const dial of TRUNK_ZERO_DIALS) {
    if (rest.startsWith(dial)) {
      const national = rest.slice(dial.length);
      if (national.startsWith('0')) {
        return `+${dial}${national.replace(/^0+/, '')}`;
      }
      return phone;
    }
  }
  return phone;
}

/**
 * Build E.164 from a picker dial code plus the digits the user typed.
 * Strips a trunk 0 and avoids double-prefixing if they pasted the country code.
 */
export function composeE164(
  dialCode: string | null | undefined,
  nationalNumber: string | null | undefined,
): string {
  const dial = normalizeDialDigits(dialCode);
  let national = String(nationalNumber || '').replace(/\D/g, '');

  if (dial && national.startsWith(dial) && national.length > dial.length + 5) {
    national = national.slice(dial.length);
  }
  if (national.startsWith('0') && (TRUNK_ZERO_DIALS as readonly string[]).includes(dial)) {
    national = national.replace(/^0+/, '');
  }
  if (!dial) return national ? `+${national}` : '';
  return `+${dial}${national}`;
}
